import { Inject, Injectable } from '@angular/core';
import { Storage, ref, getDownloadURL, uploadBytes, listAll, deleteObject } from '@angular/fire/storage';
import { FileType } from '../state-management/model/enums/state.enums';
import { DEFAULT_ROOT_FOLDER } from '../state-management/constants/global.constants';
import { ImageFormat } from '../state-management/model/enums/screen-size.enums';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class ImageUploadService {

  constructor(
    @Inject(Storage) private storage: Storage,
    private logger: LoggerService
  ) { }

  async uploadImageBlob(
    blob: Blob,
    folderPath: string,
    fileName: string,
    fileTypes: FileType[] = [FileType.PNG],
    metadata?: Record<string, string>
  ): Promise<Array<{ type: FileType, url: string }>> {
    const MAX_RETRIES = 3;
    const TIMEOUT = 30000; // 30 Sekunden

    const uploadWithRetry = async (fileType: FileType, retryCount = 0): Promise<{ type: FileType, url: string }> => {
      const fileRef = ref(this.storage, `${folderPath}/${fileName}.${fileType}`);

      const uploadPromise = new Promise<{ type: FileType, url: string }>(async (resolve, reject) => {
        try {
          this.logger.log(`Attempt ${retryCount + 1}: Uploading file type ${fileType} to ${fileRef.fullPath}`);
          const snapshot = await uploadBytes(fileRef, blob, { contentType: `image/${fileType}`, customMetadata: metadata });
          const downloadURL = await getDownloadURL(snapshot.ref);
          this.logger.log(`Uploaded file type ${fileType} to ${downloadURL}`);
          resolve({ type: fileType, url: downloadURL });
        } catch (error) {
          reject(error);
        }
      });

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error(`Upload timeout for ${fileType}`)), TIMEOUT);
      });

      try {
        return await Promise.race([uploadPromise, timeoutPromise]);
      } catch (error) {
        this.logger.error(`Upload failed for file type ${fileType}`, error);
        if (retryCount < MAX_RETRIES - 1) {
          this.logger.log(`Retrying upload for ${fileType} (Attempt ${retryCount + 2}/${MAX_RETRIES})`);
          return uploadWithRetry(fileType, retryCount + 1);
        }
        throw error;
      }
    };

    const results: Array<{ type: FileType, url: string }> = [];

    for (const fileType of fileTypes) {
      try {
        const result = await uploadWithRetry(fileType);
        results.push(result);
      } catch (error) {
        this.logger.error(`Failed to upload ${fileType}:`, error);
        // You can decide here whether to continue with other file types or throw an error
        // throw error; // Uncomment this line if you want to stop the entire process on any failure
      }
    }

    if (results.length === 0) {
      throw new Error('Failed to upload all file types');
    }

    return results;
  }

  async dropStorageFolder(folderPath: string): Promise<void> {
    const folderRef = ref(this.storage, folderPath);
    const listAllFiles = async (ref: any): Promise<any[]> => {
      const listResult = await listAll(ref);
      const allFiles = [...listResult.items];

      for (const prefix of listResult.prefixes) {
        const subFiles = await listAllFiles(prefix);
        allFiles.push(...subFiles);
      }

      return allFiles;
    };
    const allFiles = await listAllFiles(folderRef);
    const deletePromises = allFiles.map((fileRef) => {
      return new Promise<void>((resolve, reject) => {
        deleteObject(fileRef).then(() => {
          this.logger.log(`Deleted file: ${fileRef.fullPath}`);
          resolve();
        }).catch((error: unknown) => {
          this.logger.error(`Error deleting file ${fileRef.fullPath}:`, error);
          reject(error);
        });
      });
    });
    await Promise.all(deletePromises);
    this.logger.log(`Deleted all files in folder: ${folderPath}`);
  }




  async retry<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    delay: number = 1000 // Millisekunden
  ): Promise<T> {
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        this.logger.warn(`Retry ${attempt} failed.`, error);

        if (attempt < maxRetries) {
          // Wartezeit zwischen den Versuchen
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    throw new Error(`Operation failed after ${maxRetries} retries: ${lastError.message}`);
  }



  getDownloadURL(path: string, timeout = 5000): Promise<string> {
    const storageRef = ref(this.storage, path);
    const timeoutPromise = new Promise<string>((_, reject) =>
      setTimeout(() => reject(new Error('Timeout while fetching download URL')), timeout)
    );

    return Promise.race([
      getDownloadURL(storageRef),
      timeoutPromise,
    ]);
  }

  getDownloadURLWithRetry(path: string, retries: number = 3, delay: number = 1000): Promise<string> {
    return this.retry(() => this.getDownloadURL(path), retries, delay);
  }



  // getDownloadURL(path: string): Promise<string> {
  //   return getDownloadURL(ref(this.storage, path));
  // }

  async getDownloadURLs(path: string, fileTyps: string[]): Promise<Array<{ type: string, url: string }>> {
    const downloadURLs: Array<{ type: string, url: string }> = [];
    const promises = fileTyps.map(async (fileType) => {
      const downloadURL = await this.getDownloadURL(`${path}.${fileType}`);
      const res = { type: fileType, url: downloadURL };
      downloadURLs.push(res);
    });

    try {
      await Promise.all(promises);
    } catch (error: any) {
      throw new Error(`Failed to get download URL for one or more paths: ${error.message}`);
    }

    return downloadURLs;
  }

  // Helper function to create a timeout promise
  public createTimeout(timeout: number): Promise<never> {
    return new Promise((_, reject) => {
      setTimeout(() => reject('Timeout'), timeout);
    });
  }

  public getRootImagePath(shopID: string, elemId: string): string | undefined {
    return `${shopID}/${DEFAULT_ROOT_FOLDER}/${elemId}/`;
  }

  public getImageFileName(format: ImageFormat): string {
    return `image_${format}`;
  }

}